import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, Loader2, MessageCircle, MessageSquare, ZoomIn, ZoomOut, Maximize, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { lp, catalogueLabel } from "../lib/localize";
import ProductPopup from "../components/ProductPopup";
import CatalogueGrid from "./CatalogueGrid";
import RichText from "../components/RichText";
import { FadeUp } from "../components/motion";
import { SEO } from "../components/SEO";
import { toast } from "sonner";
import { CallRequestDialog } from "../components/CallRequestDialog";

const CATALOGUES = ["1.0", "4.0", "4.1", "4.2", "4.3", "4.4", "4.5", "4.6", "4.7", "4.8", "4.9", "4.10", "4.11", "4.17", "4.18", "4.19", "4.20", "4.21", "4.22", "4.23", "5.0"];
const CAT_KEY = "interactive_catalogue_no_v1";

// Visionneuse de catalogue interactif : pages du flyer pleine largeur + zones cliquables
export default function CatalogueInteractif() {
  const [catNo, setCatNo] = useState(() => {
    const urlCat = new URLSearchParams(window.location.search).get("cat");
    return urlCat || sessionStorage.getItem(CAT_KEY) || "1.0";
  });
  const [enabledCats, setEnabledCats] = useState(CATALOGUES);
  const [meta, setMeta] = useState(null);          // index + liste des pages (léger)
  const [page, setPage] = useState(null);          // page courante (image + zones)
  const pageCache = useRef({});                    // cache local des pages déjà chargées
  const [pageIdx, setPageIdx] = useState(0);  // toujours ouvrir sur la 1re page du catalogue
  const [popupPid, setPopupPid] = useState(null);
  const [popupSize, setPopupSize] = useState(null);
  const [popupFinish, setPopupFinish] = useState(null);
  const [flashPid, setFlashPid] = useState(null);
  const [query, setQuery] = useState("");
  const [jump, setJump] = useState("");
  const [showZones, setShowZones] = useState(true);
  const { lang, t, ts } = useLang();

  // Zoom de la page courante (les zones cliquables suivent la transformation)
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const movedRef = useRef(false);
  const [zoom, setZoom] = useState({ z: 1, x: 0, y: 0 });

  const clampT = useCallback((z, x, y) => {
    const el = stageRef.current;
    if (!el) return { x, y };
    return {
      x: Math.min(0, Math.max(-(z - 1) * el.clientWidth, x)),
      y: Math.min(0, Math.max(-(z - 1) * el.clientHeight, y)),
    };
  }, []);

  const zoomBy = useCallback((factor, cx, cy) => {
    setZoom(({ z, x, y }) => {
      const z2 = Math.min(5, Math.max(1, z * factor));
      if (z2 === 1) return { z: 1, x: 0, y: 0 };
      let nx = x, ny = y;
      const el = stageRef.current;
      if (el && cx !== undefined) {
        const r = el.getBoundingClientRect();
        const px = cx - r.left, py = cy - r.top;
        nx = px - (px - x) * (z2 / z);
        ny = py - (py - y) * (z2 / z);
      }
      return { z: z2, ...clampT(z2, nx, ny) };
    });
  }, [clampT]);

  // molette : zoom centré sur le curseur (non passif pour bloquer le défilement)
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e) => { e.preventDefault(); zoomBy(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX, e.clientY); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomBy, page]);

  // masque les catalogues désactivés dans l'admin
  useEffect(() => {
    api.get("/settings/shop").then((res) => {
      const disabled = res.data.disabled_catalogues || [];
      const enabled = CATALOGUES.filter((c) => !disabled.includes(c));
      setEnabledCats(enabled);
      if (enabled.length && !enabled.includes(sessionStorage.getItem(CAT_KEY) || "1.0")) {
        switchCatalogue(enabled[0]);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // charge l'index du catalogue + la liste des pages (léger), pas les pages elles-mêmes
  useEffect(() => {
    if (catNo === "5.0") return;  // catalogue web (Urbano) : pas de PDF interactif, vue grille
    setMeta(null);
    setPage(null);
    api.get(`/catalogue-interactif/${catNo}`, { params: { light: 1 } })
      .then((res) => { setMeta(res.data); setPageIdx(0); })
      .catch(() => setMeta({ pages_meta: [], products_index: [] }));
  }, [catNo]);

  const switchCatalogue = (no) => {
    setCatNo(no);
    sessionStorage.setItem(CAT_KEY, no);
    setPageIdx(0);  // retour à la 1re page à chaque changement de catalogue
    setQuery("");
  };

  const pagesMeta = useMemo(() => meta?.pages_meta || [], [meta]);
  const pageNumber = pagesMeta[Math.min(pageIdx, Math.max(0, pagesMeta.length - 1))]?.page;

  // charge uniquement la page courante (cache local + annulation)
  useEffect(() => {
    if (!pageNumber) return;
    const key = `${catNo}:${pageNumber}`;
    if (pageCache.current[key]) {
      setPage(pageCache.current[key]);
      return;
    }
    setPage(null);
    let cancelled = false;
    api.get(`/catalogue-interactif/${catNo}/page/${pageNumber}`)
      .then((res) => {
        pageCache.current[key] = res.data;
        if (!cancelled) setPage(res.data);
      })
      .catch(() => { if (!cancelled) setPage(null); });
    return () => { cancelled = true; };
  }, [catNo, pageNumber]);

  const goTo = useCallback((idx) => {
    if (!pagesMeta.length) return;
    const clamped = Math.max(0, Math.min(pagesMeta.length - 1, idx));
    setPageIdx(clamped);
    setShowZones(true);
    setZoom({ z: 1, x: 0, y: 0 });
  }, [pagesMeta.length]);

  // Flash des zones à chaque changement de page (repérage), puis survol seulement
  useEffect(() => {
    if (!showZones) return;
    const id = setTimeout(() => setShowZones(false), 1600);
    return () => clearTimeout(id);
  }, [showZones, pageIdx]);

  // Précharge les pages voisines (JSON léger + image)
  useEffect(() => {
    [pageIdx - 1, pageIdx + 1].forEach((i) => {
      const pm = pagesMeta[i];
      if (!pm) return;
      const key = `${catNo}:${pm.page}`;
      const cached = pageCache.current[key];
      if (cached) {
        const im = new Image(); im.src = cached.image;
        return;
      }
      api.get(`/catalogue-interactif/${catNo}/page/${pm.page}`)
        .then((res) => {
          pageCache.current[key] = res.data;
          const im = new Image(); im.src = res.data.image;
        })
        .catch(() => {});
    });
  }, [pageIdx, pagesMeta, catNo]);

  // Clavier : flèches gauche/droite
  useEffect(() => {
    const onKey = (e) => {
      if (popupPid) return;
      if (e.key === "ArrowLeft") goTo(pageIdx - 1);
      if (e.key === "ArrowRight") goTo(pageIdx + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageIdx, goTo, popupPid]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2 || !meta) return [];
    return meta.products_index
      .filter((p) => [p.name, p.name_en, p.sku, p.category, p.category_en]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [query, meta]);

  const jumpToProduct = (p) => {
    const idx = pagesMeta.findIndex((pg) => pg.page === p.catalogue_page);
    if (idx >= 0) {
      goTo(idx);
      setFlashPid(p.id);
      setTimeout(() => setFlashPid(null), 3000);
    }
    setQuery("");
  };

  const submitJump = (e) => {
    e.preventDefault();
    const n = parseInt(jump);
    if (Number.isNaN(n)) return;
    // accepte le numéro imprimé sur la page (label) ou le numéro interne
    const idx = pagesMeta.findIndex((pg) => pg.label === n || pg.page === n);
    if (idx >= 0) { goTo(idx); setJump(""); }
    else toast.error(t("catalogue.page_not_found", { n }));
  };

  return (
    <div data-testid="interactive-catalogue" className="pt-28 pb-16 min-h-screen">
      {/* Page utilitaire (zones cliquables d'un catalogue PDF) : contenu non
          lisible par les robots → noindex ; la valeur SEO vit sur /catalogues */}
      <SEO title={`${ts("seo.catalogue_title")} — ${catalogueLabel(catNo)}`} description={ts("seo.catalogue_desc")} path="/catalogue-interactif" noindex />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Link to="/catalogues" data-testid="viewer-back-catalogues"
          className="inline-flex items-center gap-2 text-sm text-ivory/60 hover:text-gold transition-colors duration-300 mb-6">
          <ArrowLeft className="w-4 h-4" /> {t("catalogues.viewer_back")}
        </Link>
        <FadeUp className="mb-8">
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{catalogueLabel(catNo)}</p>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight mb-3">
            {t("catalogue.title_1")} <em className="italic text-gold">{t("catalogue.title_2")}</em>
          </h1>
          <p className="text-ivory/60 max-w-2xl">{t("catalogue.click_hint")}</p>
        </FadeUp>

        {/* Comment acheter + soumission rapide */}
        <FadeUp delay={0.05} className="mb-10">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface border border-ivory/10 rounded-xl p-6" data-testid="catalogue-howto">
              <h2 className="font-serif text-xl text-champagne mb-4">{t("catalogue.howto_title")}</h2>
              <ol className="space-y-2.5">
                {t("catalogue.howto_steps").map((s, i) => (
                  <li key={i} data-testid={`catalogue-howto-step-${i + 1}`} className="flex gap-3 text-sm text-ivory/70 leading-snug">
                    <span className="shrink-0 w-5 h-5 rounded-full border border-gold/50 text-gold text-[11px] flex items-center justify-center font-semibold">{i + 1}</span>
                    <RichText text={s} path={`catalogue.howto_steps.${i}`} />
                  </li>
                ))}
              </ol>
              <p className="mt-4 pt-4 border-t border-ivory/10 text-sm text-gold/90 leading-snug" data-testid="catalogue-howto-delays">
                {t("catalogue.howto_delays")}
              </p>
            </div>
            <div className="bg-surface border border-gold/25 rounded-xl p-6 spotlight flex flex-col justify-center" data-testid="catalogue-quote">
              <h2 className="font-serif text-xl text-gold mb-2">{t("catalogue.quote_title")}</h2>
              <p className="text-sm text-ivory/60 mb-5">{t("catalogue.quote_text")}</p>
              <div className="flex flex-wrap gap-3">
                <a href="https://m.me/trophee.ca" target="_blank" rel="noreferrer" data-testid="quote-messenger"
                  className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
                  <MessageCircle className="w-4 h-4" /> {t("catalogue.quote_messenger")}
                </a>
                <a href="sms:+15145656665" data-testid="quote-sms"
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
                  <MessageSquare className="w-4 h-4" /> {t("catalogue.quote_sms")}
                </a>
                <CallRequestDialog testid="quote-call" />
              </div>
            </div>
          </div>
        </FadeUp>

        {/* Sélecteur de catalogue */}
        <FadeUp delay={0.05} className="mb-6">
          <div className="flex flex-wrap gap-3" data-testid="catalogue-tabs">
            {enabledCats.map((no) => (
              <button key={no} onClick={() => switchCatalogue(no)} data-testid={`catalogue-tab-${no}`}
                className={`px-5 py-2.5 rounded-full text-sm border transition-colors duration-300 ${
                  catNo === no ? "border-gold bg-gold text-ink font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/50 hover:text-gold"
                }`}>
                {catalogueLabel(no)}
              </button>
            ))}
          </div>
        </FadeUp>

        {catNo === "5.0" && <CatalogueGrid catalogueNo="5.0" />}

        {catNo !== "5.0" && (<>
        {/* Recherche → saut à la page */}
        <FadeUp delay={0.1} className="mb-6">
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory/65" />
            <input
              data-testid="catalogue-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={ts("catalogue.search_placeholder")}
              className="w-full bg-surface border border-ivory/15 rounded-full pl-11 pr-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
            />
            {matches.length > 0 && (
              <div className="absolute z-20 top-full mt-2 left-0 right-0 bg-surface border border-gold/25 rounded-xl overflow-hidden shadow-2xl" data-testid="search-matches">
                <p className="text-[11px] text-ivory/65 px-4 pt-3">{t("catalogue.matches", { n: matches.length })}</p>
                {matches.map((p) => (
                  <button key={p.id} onClick={() => jumpToProduct(p)} data-testid={`search-match-${p.id}`}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gold/10 hover:text-gold transition-colors duration-200 flex items-center justify-between gap-3">
                    <span className="truncate">{lp(p, lang).name}</span>
                    <span className="text-xs text-ivory/65 shrink-0">{p.sku} · {t("catalogue.page_label")} {(pagesMeta.find((pg) => pg.page === p.catalogue_page) || {}).label || p.catalogue_page}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </FadeUp>

        {!meta ? (
          <div className="flex items-center justify-center py-32" data-testid="catalogue-loading">
            <Loader2 className="w-8 h-8 animate-spin text-gold" />
          </div>
        ) : !page ? (
          <div className="flex items-center justify-center py-32" data-testid="catalogue-page-loading">
            <Loader2 className="w-8 h-8 animate-spin text-gold" />
          </div>
        ) : (
          <FadeUp delay={0.15}>
            <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
              <button onClick={() => goTo(pageIdx - 1)} disabled={pageIdx === 0} data-testid="page-prev"
                className="inline-flex items-center gap-2 border border-ivory/20 text-ivory/80 px-5 py-2.5 rounded-full text-sm hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" /> {t("catalogue.prev_page")}
              </button>
              <div className="flex items-center gap-3">
                <p className="text-sm text-ivory/60" data-testid="page-indicator">
                  {t("catalogue.page_of", { n: page.label || page.page, total: pagesMeta[pagesMeta.length - 1]?.label || pagesMeta[pagesMeta.length - 1]?.page })}
                </p>
                <form onSubmit={submitJump} className="flex items-center gap-2">
                  <input type="number" min="1" value={jump} onChange={(e) => setJump(e.target.value)}
                    data-testid="page-jump-input" placeholder={ts("catalogue.page_label")}
                    className="w-20 bg-surface border border-ivory/15 rounded-lg px-3 py-1.5 text-sm text-center focus:border-gold focus:outline-none" />
                  <button type="submit" data-testid="page-jump-btn"
                    className="text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                    {t("catalogue.goto")}
                  </button>
                </form>
              </div>
              <button onClick={() => goTo(pageIdx + 1)} disabled={pageIdx >= pagesMeta.length - 1} data-testid="page-next"
                className="inline-flex items-center gap-2 border border-ivory/20 text-ivory/80 px-5 py-2.5 rounded-full text-sm hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-30">
                {t("catalogue.next_page")} <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Page du catalogue : le flyer occupe toute la largeur d'écran disponible (zoom molette / boutons / glisser) */}
            <div ref={stageRef}
              className={`relative mx-auto w-full select-none overflow-hidden rounded-lg border border-ivory/10 bg-white touch-none ${zoom.z > 1 ? "cursor-grab active:cursor-grabbing" : ""}`}
              data-testid="catalogue-stage"
              onPointerDown={(e) => {
                if (e.target.closest("button")) return; // laisse passer les clics (zoom, zones)
                dragRef.current = { sx: e.clientX, sy: e.clientY, x: zoom.x, y: zoom.y };
                movedRef.current = false;
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (!dragRef.current) return;
                const dx = e.clientX - dragRef.current.sx, dy = e.clientY - dragRef.current.sy;
                if (Math.abs(dx) + Math.abs(dy) > 4) movedRef.current = true;
                if (zoom.z > 1) setZoom(({ z }) => ({ z, ...clampT(z, dragRef.current.x + dx, dragRef.current.y + dy) }));
              }}
              onPointerUp={() => { dragRef.current = null; }}>
              <div className="relative w-full" style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.z})`, transformOrigin: "0 0" }}>
                <img key={page.page} src={page.image} alt={`${catalogueLabel(catNo)} — page ${page.page}`}
                  data-testid="catalogue-page-image" draggable="false"
                  onDoubleClick={() => zoomBy(zoom.z === 1 ? 2.5 : 1 / 2.5)}
                  className="w-full" />
                {page.hotspots.filter((h) => h.pid).map((h, i) => (
                  <button
                    key={`${h.pid}-${i}`}
                    data-testid={`hotspot-${h.pid}-${i}`}
                    onClick={() => {
                      if (movedRef.current) { movedRef.current = false; return; }
                      setPopupPid(h.pid); setPopupSize(h.size_id || null); setPopupFinish(h.finish_id || null);
                    }}
                    aria-label="Voir le produit"
                    className={`absolute rounded-md transition-all duration-300 cursor-pointer ${
                      flashPid === h.pid
                        ? "border-2 border-gold bg-gold/25 shadow-[0_0_30px_rgba(201,162,77,0.5)] animate-pulse"
                        : showZones
                          ? "border-2 border-gold/70 bg-gold/10"
                          : "border-2 border-transparent hover:border-gold hover:bg-gold/15"
                    }`}
                    style={{
                      left: `${(h.x / page.width) * 100}%`,
                      top: `${(h.y / page.height) * 100}%`,
                      width: `${(h.w / page.width) * 100}%`,
                      height: `${(h.h / page.height) * 100}%`,
                    }}
                  />
                ))}
              </div>
              {/* Contrôles de zoom */}
              <div className="absolute top-3 right-3 flex flex-col items-center gap-1.5 z-10">
                <button type="button" onClick={() => zoomBy(1.4)} data-testid="catalogue-zoom-in" aria-label={ts("catalogue.zoom_in")}
                  className="w-10 h-10 rounded-full bg-ink/80 text-gold border border-gold/40 flex items-center justify-center hover:bg-gold hover:text-ink transition-colors duration-300">
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => zoomBy(0.7)} data-testid="catalogue-zoom-out" aria-label={ts("catalogue.zoom_out")}
                  className="w-10 h-10 rounded-full bg-ink/80 text-gold border border-gold/40 flex items-center justify-center hover:bg-gold hover:text-ink transition-colors duration-300">
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setZoom({ z: 1, x: 0, y: 0 })} data-testid="catalogue-zoom-reset" aria-label={ts("catalogue.zoom_reset")}
                  className="w-10 h-10 rounded-full bg-ink/80 text-gold border border-gold/40 flex items-center justify-center hover:bg-gold hover:text-ink transition-colors duration-300">
                  <Maximize className="w-4 h-4" />
                </button>
                {zoom.z > 1 && (
                  <span className="text-[10px] font-semibold bg-ink/80 text-gold border border-gold/40 rounded-full px-2 py-0.5" data-testid="catalogue-zoom-level">
                    {Math.round(zoom.z * 100)} %
                  </span>
                )}
              </div>
            </div>
          </FadeUp>
        )}
        </>)}
      </div>

      {popupPid && <ProductPopup productId={popupPid} initialSizeId={popupSize} initialFinishId={popupFinish} onClose={() => setPopupPid(null)} />}
    </div>
  );
}
